/**
 * Session Idle-Timeout Policy hooks (Issue #289).
 *
 * Wraps the typed `@jinzhuan/api-client` calls so the `/settings`
 * session policy section can read and update the idle-timeout setting.
 *
 * 红线：
 *   - 所有数据通过 `@jinzhuan/api-client` typed client 调用。
 *   - 非 2xx 错误必须冒泡给操作人，不静默吞错。
 */
import { useCallback, useEffect, useState } from 'react';
import {
  type SessionPolicyResponse,
  type UpdateSessionPolicyRequest,
} from '@jinzhuan/api-client';
import { getStoredAccessToken } from '@/lib/tokenStorage';
import { createSessionAwareApiClient } from '@/lib/createSessionAwareApiClient';

// Issue #289: use the session-aware wrapper so a backend-reported idle
// expiration (401 with `session idle timeout expired` body) fires the
// centralized session-expiry signal, which `AuthContext` reacts to by
// clearing local auth state and redirecting to `/login`.
const apiClient = createSessionAwareApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybeMessage = (err as { message?: string }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return fallback;
}

export interface SessionPolicyCenterReturn {
  readonly policy: AsyncState<SessionPolicyResponse>;
  refresh: () => Promise<void>;
  updatePolicy: (input: UpdateSessionPolicyRequest) => Promise<SessionPolicyResponse>;
}

export type { SessionPolicyResponse, UpdateSessionPolicyRequest };

export function useSessionPolicyCenter(): SessionPolicyCenterReturn {
  const [policy, setPolicy] = useState<AsyncState<SessionPolicyResponse>>({
    data: null,
    loading: true,
    error: null,
  });

  const fetchPolicy = useCallback(async () => {
    setPolicy((p) => ({ ...p, loading: true, error: null }));
    try {
      const res = await apiClient.GET('/api/v1/admin/session-policy');
      if (!res.response.ok || res.data === undefined) {
        const ct = res.response.headers.get('content-type') ?? '';
        const bodyText = ct.includes('application/json')
          ? JSON.stringify(res.error)
          : `HTTP ${res.response.status}`;
        setPolicy({ data: null, loading: false, error: bodyText });
        return;
      }
      setPolicy({ data: res.data, loading: false, error: null });
    } catch (err) {
      setPolicy({
        data: null,
        loading: false,
        error: pickErrorMessage(err, '加载会话策略失败'),
      });
    }
  }, []);

  useEffect(() => {
    void fetchPolicy();
  }, [fetchPolicy]);

  const updatePolicy = useCallback(
    async (input: UpdateSessionPolicyRequest): Promise<SessionPolicyResponse> => {
      const res = await apiClient.PATCH('/api/v1/admin/session-policy', { body: input });
      if (!res.response.ok || res.data === undefined) {
        const ct = res.response.headers.get('content-type') ?? '';
        const msg = ct.includes('application/json')
          ? JSON.stringify(res.error)
          : `HTTP ${res.response.status}`;
        throw new Error(msg);
      }
      await fetchPolicy();
      return res.data;
    },
    [fetchPolicy],
  );

  return {
    policy,
    refresh: fetchPolicy,
    updatePolicy,
  };
}
